import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          (function () {
  function initShopifyCarousels() {
    document.querySelectorAll(
      '[data-shopify-carousel]:not([data-initialized])'
    ).forEach(function (card) {
      card.setAttribute('data-initialized', 'true');

      const slides = Array.from(card.querySelectorAll('.slide'));
      const lines = card.querySelector('.lines');
      const bars = lines
        ? Array.from(lines.querySelectorAll('button'))
        : [];

      const n = slides.length;

      if (!n) return;

      if (n === 1) {
        slides[0].style.transform = 'translateX(0)';
        return;
      }

      const half = Math.floor(n / 2);
      const pos = new Array(n).fill(0);

      let i = 0;
      let x0 = 0;
      let dx = 0;
      let down = false;
      let activePointerId = null;

      function place(k, p, teleport) {
        const slide = slides[k];

        if (teleport) {
          slide.style.transition = 'none';
        }

        slide.style.transform =
          'translateX(calc(' + (p * 100) + '% + var(--dx, 0px)))';

        pos[k] = p;

        if (teleport) {
          void slide.offsetWidth;
          slide.style.transition = '';
        }
      }

      function updateDots() {
        const windowSize = Math.min(3, n);

        const start = Math.min(
          Math.max(i - 1, 0),
          Math.max(n - windowSize, 0)
        );

        const end = Math.min(start + windowSize, n) - 1;

        if (lines) {
          lines.style.width = windowSize * 14 + 'px';
        }

        bars.forEach(function (button, k) {
          let size = 6;

          if (k < start || k > end) {
            size = 0;
          } else if (
            (k === start && start > 0) ||
            (k === end && end < n - 1)
          ) {
            size = 3;
          } else if (
            (k === start + 1 && start > 0) ||
            (k === end - 1 && end < n - 1)
          ) {
            size = 5;
          }

          if (k === i) {
            size = 8;
          }

          button.style.setProperty('--s', size + 'px');
          button.style.transform =
            'translateX(' + (-start * 14) + 'px)';

          button.classList.toggle('on', k === i);

          if (k === i) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      function go(x, dir) {
        dir = dir || 1;

        const old = i;
        i = (x + n) % n;

        slides.forEach(function (_, k) {
          let p = ((k - i + n + half) % n) - half;

          if (k === i && Math.abs(pos[k]) > 1) {
            place(k, dir, true);
          }

          if (k === old && k !== i && Math.abs(p) > 1) {
            p = -dir;
          }

          place(k, p, Math.abs(p - pos[k]) > 1);
        });

        updateDots();
      }

      bars.forEach(function (button, k) {
        button.addEventListener('click', function () {
          const delta = ((k - i + n + half) % n) - half;

          if (delta) {
            go(k, delta > 0 ? 1 : -1);
          }
        });
      });

      const prev = card.querySelector('.arrow.prev');
      const next = card.querySelector('.arrow.next');

      if (prev) {
        prev.addEventListener('click', function () {
          go(i - 1, -1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          go(i + 1, 1);
        });
      }

      card.addEventListener('pointerdown', function (e) {
        if (e.target.closest('.lines, .arrow')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        down = true;
        activePointerId = e.pointerId;
        x0 = e.clientX;
        dx = 0;

        card.classList.add('drag');

        try {
          card.setPointerCapture(e.pointerId);
        } catch (error) {}
      });

      card.addEventListener('pointermove', function (e) {
        if (!down || e.pointerId !== activePointerId) return;

        dx = e.clientX - x0;
        card.style.setProperty('--dx', dx + 'px');
      });

      function end(e) {
        if (!down) return;

        if (
          e &&
          activePointerId !== null &&
          e.pointerId !== activePointerId
        ) {
          return;
        }

        down = false;
        activePointerId = null;

        card.classList.remove('drag');
        card.style.setProperty('--dx', '0px');

        if (Math.abs(dx) > 40) {
          go(
            dx < 0 ? i + 1 : i - 1,
            dx < 0 ? 1 : -1
          );
        } else {
          go(i);
        }

        dx = 0;
      }

      card.addEventListener('pointerup', end);
      card.addEventListener('pointercancel', end);
      card.addEventListener('lostpointercapture', end);

      slides.forEach(function (_, k) {
        place(k, ((k + half) % n) - half, true);
      });

      go(0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initShopifyCarousels
    );
  } else {
    initShopifyCarousels();
  }

  document.addEventListener(
    'shopify:section:load',
    initShopifyCarousels
  );
})();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
